<template>
  <div class="flex items-center gap-2">
    <DropdownMenu>
      <DropdownMenuTrigger as-child>
        <Button variant="outline" size="sm" type="button" class="h-7 rounded-full border-transparent bg-muted px-3 text-xs font-semibold text-muted-foreground shadow-none ring-1 ring-border hover:bg-transparent" :loading="isUpdating">
          <span v-if="showLabel" class="text-xs text-muted-foreground">
            Start date
          </span><span class="truncate">{{ startAtLabel }}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" class="w-56">
        <DropdownMenuLabel>Set Start Date:</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetStartAt('today')">
          Today
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetStartAt('tomorrow')">
          Tomorrow
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetStartAt('end_of_week')">
          End of Week
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="setPresetStartAt('next_week')">
          Next Week
        </DropdownMenuItem>
        <DropdownMenuItem :disabled="isUpdating" @select="openCustomModal">
          Custom
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>

    <TaskCustomStartAtModal
      ref="customStartAtModal"
      :task-id="taskId"
      :value="value"
      :context="context"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import { Button } from '../../../../../../../../resources/js/components/ui/button/index.js'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../../../../../../../../resources/js/components/ui/dropdown-menu/index.js'
import TaskCustomStartAtModal from './TaskCustomStartAtModal.vue'
import { useTasksStore } from '../../../stores/tasksStore.js'
import { buildTaskPresetDate, formatTaskFieldDateTimeLabel } from '../../../utils/taskFieldDateTime.js'

const props = defineProps({
  task: {
    type: Object,
    required: true,
  },
  value: {
    type: [String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'default',
  },
  showLabel: {
    type: Boolean,
    default: false,
  },
})

const customStartAtModal = ref(null)

const tasksStore = useTasksStore(props.context)
const taskId = computed(() => Number(props.task?.id))
const isUpdating = computed(() => tasksStore.isUpdatingStartAt(taskId.value))
const startAtLabel = computed(() => formatTaskFieldDateTimeLabel(props.value, 'No start date'))

const updateTaskStartAt = async (startAt, successMessage = 'Start date updated.') => {
  if (!Number.isInteger(taskId.value) || taskId.value <= 0) {
    return false
  }

  try {
    await tasksStore.updateStartAt(taskId.value, startAt)
    toast.success(successMessage)
    return true
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.start_at?.[0]
      || 'Unable to update the start date.'
    toast.error(errorMessage)
    return false
  }
}

const setPresetStartAt = async (preset) => {
  const presetDate = buildTaskPresetDate(preset, 9)

  if (!presetDate) {
    return
  }

  const successMessagesByPreset = {
    today: 'Start date set to today.',
    tomorrow: 'Start date set to tomorrow.',
    end_of_week: 'Start date set to end of week.',
    next_week: 'Start date set to next week.',
  }

  await updateTaskStartAt(
    presetDate.toISOString(),
    successMessagesByPreset[preset] ?? 'Start date updated.'
  )
}
const openCustomModal = () => {
  customStartAtModal.value?.openModal?.()
}
</script>
